import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input, Dropdown, Option, Button, Spinner, MessageBar, MessageBarBody, Text, Tooltip, TabList, Tab, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Field, Badge, Textarea, } from "@fluentui/react-components";
import { SearchRegular, ChevronLeftRegular, ChevronRightRegular, ArrowSyncRegular, CheckmarkCircleRegular, DismissCircleRegular, DeleteRegular, PersonRegular, } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { getAllLeaveRequests, getAllJoursFeries, getAllApprobateurs, updateLeaveRequestStatus, createLeaveRequest, getSalaries, } from "../../../services/dataService";
const JOURS_COURTS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
const JOURS_COMPLETS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const TYPE_COLORS = {
    CP: { bg: "#00bfff", text: "#000", label: "CONGES PAYES" },
    CPSEQ: { bg: "#00bfff", text: "#000", label: "CP SÉQUENTIELS" },
    RTT: { bg: "#00cc66", text: "#000", label: "RTT" },
    JA: { bg: "#9966ff", text: "#fff", label: "JOURS D'ANCIENNETE" },
    CSS: { bg: "#9ca3af", text: "#000", label: "CONGÉ SANS SOLDE" },
    CSF: { bg: "#f59e0b", text: "#000", label: "CONGÉ SOLIDARITÉ FAMILLE" },
    CPP: { bg: "#ec4899", text: "#fff", label: "CONGÉ PARENTALITÉ" },
    EFDEM: { bg: "#f97316", text: "#000", label: "ÉVÉNEMENT FAMILIAL DÉMENAGEMENT" },
    EFMAR: { bg: "#f97316", text: "#000", label: "ÉVÉNEMENT FAMILIAL MARIAGE" },
    REC: { bg: "#06b6d4", text: "#000", label: "RÉCUPÉRATION" },
    RECJP: { bg: "#06b6d4", text: "#000", label: "RÉCUPÉRATION JP" },
    TELET: { bg: "#84cc16", text: "#000", label: "TÉLÉTRAVAIL" },
    MISS: { bg: "#a855f7", text: "#fff", label: "MISSION" },
    ASTRE: { bg: "#ef4444", text: "#fff", label: "ASTREINTE" },
};
const LEAVE_TYPE_OPTIONS = Object.entries(TYPE_COLORS).map(([code, info]) => ({
    value: code,
    label: `${code} — ${info.label}`,
}));
const JF_COLOR = { bg: "#000080", text: "#fff" };
const PONT_COLOR = { bg: "#7c3aed", text: "#fff" };
const WE_COLOR = { bg: "#f0f0f0", text: "#999" };
const TODAY_BORDER = "3px solid #ef4444";
const PENDING_STRIPE = "repeating-linear-gradient(45deg, rgba(0,0,0,0.15), rgba(0,0,0,0.15) 3px, transparent 3px, transparent 6px)";
function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}
function toISO(year, month, day) {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function formatDateFR(iso) {
    if (!iso)
        return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}
function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d;
}
function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}
function countBusinessDays(start, end) {
    let count = 0;
    const cur = new Date(start);
    const endD = new Date(end);
    while (cur <= endD) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6)
            count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}
function buildAbsenceMap(requests) {
    const map = new Map();
    for (const req of requests) {
        const isPending = req.status.startsWith("En attente");
        const isApproved = req.status === "Approuvé" || req.status === "Approuve";
        if (!isApproved && !isPending)
            continue;
        const start = new Date(req.startDate);
        const end = new Date(req.endDate);
        const current = new Date(start);
        while (current <= end) {
            const iso = toISO(current.getFullYear(), current.getMonth(), current.getDate());
            map.set(iso, { request: req, isPending });
            current.setDate(current.getDate() + 1);
        }
    }
    return map;
}
function buildFeriesMap(feries, year) {
    const map = new Map();
    for (const jf of feries) {
        // Extraire l'année de la date si annee est 0
        const jfYear = jf.annee || (jf.dateFerie ? parseInt(jf.dateFerie.substring(0, 4), 10) : 0);
        if (jfYear === year || jf.recurrent) {
            let dateKey = jf.dateFerie;
            if (jf.recurrent && dateKey.length >= 10) {
                dateKey = `${year}${dateKey.substring(4)}`;
            }
            const isPont = (jf.libelle || "").toLowerCase().includes("pont");
            map.set(dateKey, { libelle: jf.libelle, isPont });
        }
    }
    return map;
}
function lightenColor(hex, amount) {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, ((num >> 16) & 0xff) + Math.round((255 - ((num >> 16) & 0xff)) * amount));
    const g = Math.min(255, ((num >> 8) & 0xff) + Math.round((255 - ((num >> 8) & 0xff)) * amount));
    const b = Math.min(255, (num & 0xff) + Math.round((255 - (num & 0xff)) * amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
const todayISO = (() => { const d = new Date(); return toISO(d.getFullYear(), d.getMonth(), d.getDate()); })();
function getCellStyle(iso, dow, feriesMap, absenceMap) {
    const isWeekend = dow === 0 || dow === 6;
    const ferie = feriesMap.get(iso);
    const absence = absenceMap.get(iso);
    const dayLabel = JOURS_COURTS[dow === 0 ? 6 : dow - 1];
    const isToday = iso === todayISO;
    let bg = "#fff";
    let color = "#374151";
    let label = dayLabel;
    let tooltip = "";
    let fontWeight = "normal";
    let isPending = false;
    let border = isToday ? TODAY_BORDER : "";
    if (isWeekend) {
        bg = WE_COLOR.bg;
        color = WE_COLOR.text;
    }
    if (ferie) {
        if (ferie.isPont) {
            bg = PONT_COLOR.bg;
            color = PONT_COLOR.text;
            label = "P";
            tooltip = ferie.libelle;
        }
        else {
            bg = JF_COLOR.bg;
            color = JF_COLOR.text;
            label = "JF";
            tooltip = ferie.libelle;
        }
        fontWeight = "bold";
    }
    if (absence) {
        const typeColor = TYPE_COLORS[absence.request.leaveType] || { bg: "#6b7280", text: "#fff" };
        label = absence.request.leaveType;
        fontWeight = "bold";
        if (absence.isPending) {
            bg = lightenColor(typeColor.bg, 0.55);
            color = "#555";
            tooltip = `${absence.request.leaveType} — EN ATTENTE de validation`;
            isPending = true;
            border = `2px dashed ${typeColor.bg}`;
        }
        else {
            bg = typeColor.bg;
            color = typeColor.text;
            tooltip = `${absence.request.leaveType} — Approuvé`;
        }
    }
    return { bg, color, label, tooltip, fontWeight, isPending, border };
}
export const PlanningView = ({ onNavigateToFiche, fixedEmployeeEmail }) => {
    const { apiToken, userEmail } = useAuth();
    const [mode, setMode] = useState("year");
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth());
    const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
    const [requests, setRequests] = useState([]);
    const [feries, setFeries] = useState([]);
    const [approbateurs, setApprobateurs] = useState([]);
    const [salaries, setSalaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState(fixedEmployeeEmail || "");
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target))
                setPickerOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);
    // Dialog détail absence (clic sur une absence)
    const [detailRequest, setDetailRequest] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    // Saisie d'absence (2 clics)
    const [selectionStart, setSelectionStart] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createStartDate, setCreateStartDate] = useState("");
    const [createEndDate, setCreateEndDate] = useState("");
    const [createType, setCreateType] = useState("CP");
    const [createComment, setCreateComment] = useState("");
    const [createLoading, setCreateLoading] = useState(false);
    const showMessage = (msg, intent) => {
        if (intent === "success") {
            setSuccess(msg);
            setError(null);
        }
        else {
            setError(msg);
            setSuccess(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    };
    const fetchData = useCallback(async () => {
        if (!apiToken)
            return;
        setLoading(true);
        setError(null);
        try {
            const [reqs, jf, approbs, sals] = await Promise.all([
                getAllLeaveRequests(apiToken),
                getAllJoursFeries(apiToken),
                getAllApprobateurs(apiToken),
                getSalaries(apiToken),
            ]);
            setRequests(reqs);
            setFeries(jf);
            setApprobateurs(approbs);
            setSalaries(sals);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [apiToken]);
    useEffect(() => { fetchData(); }, [fetchData]);
    const employees = useMemo(() => {
        return salaries
            .filter((s) => s.mail)
            .map((s) => ({
            email: s.mail,
            upn: s.userPrincipalName || "",
            name: s.displayName,
            service: s.extensionAttribute3 || s.department || "",
            lieu: s.extensionAttribute2 || "",
        }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [salaries]);
    const pickerResults = useMemo(() => {
        if (!searchText || searchText.length < 2)
            return [];
        const s = searchText.toLowerCase();
        return employees.filter((e) => e.name.toLowerCase().includes(s) || e.email.toLowerCase().includes(s) || e.upn.toLowerCase().includes(s)).slice(0, 15);
    }, [employees, searchText]);
    const currentEmployee = fixedEmployeeEmail || selectedEmployee;
    // Trouver l'employé sélectionné (mail + upn pour matcher les demandes)
    const currentEmp = useMemo(() => {
        if (!currentEmployee)
            return undefined;
        const lower = currentEmployee.toLowerCase();
        return employees.find((e) => e.email.toLowerCase() === lower || e.upn.toLowerCase() === lower);
    }, [employees, currentEmployee]);
    const employeeRequests = useMemo(() => {
        if (!currentEmp)
            return [];
        const mail = currentEmp.email.toLowerCase();
        const upn = currentEmp.upn.toLowerCase();
        return requests.filter((r) => {
            const reqEmail = r.employeeEmail.toLowerCase();
            return reqEmail === mail || reqEmail === upn;
        });
    }, [requests, currentEmp]);
    const feriesMap = useMemo(() => buildFeriesMap(feries, year), [feries, year]);
    const absenceMap = useMemo(() => buildAbsenceMap(employeeRequests), [employeeRequests]);
    const approvedDays = employeeRequests.filter((r) => r.status === "Approuvé" || r.status === "Approuve").reduce((s, r) => s + r.daysCount, 0);
    const pendingDays = employeeRequests.filter((r) => r.status.startsWith("En attente")).reduce((s, r) => s + r.daysCount, 0);
    const employeeName = currentEmp?.name || "";
    // Navigation
    const navigatePrev = () => {
        if (mode === "year")
            setYear((y) => y - 1);
        else if (mode === "month") {
            if (month === 0) {
                setMonth(11);
                setYear((y) => y - 1);
            }
            else
                setMonth((m) => m - 1);
        }
        else {
            setWeekStart((d) => addDays(d, -7));
        }
    };
    const navigateNext = () => {
        if (mode === "year")
            setYear((y) => y + 1);
        else if (mode === "month") {
            if (month === 11) {
                setMonth(0);
                setYear((y) => y + 1);
            }
            else
                setMonth((m) => m + 1);
        }
        else {
            setWeekStart((d) => addDays(d, 7));
        }
    };
    const periodLabel = () => {
        if (mode === "year")
            return `Absences de l'année ${year}`;
        if (mode === "month")
            return `${MOIS[month]} ${year}`;
        const end = addDays(weekStart, 6);
        const fmt = (d) => `${d.getDate()} ${MOIS_COURTS[d.getMonth()]}`;
        return `Semaine du ${fmt(weekStart)} au ${fmt(end)} ${end.getFullYear()}`;
    };
    // === Clic sur une cellule du planning ===
    const handleCellClick = useCallback((iso) => {
        const absence = absenceMap.get(iso);
        const dow = new Date(iso).getDay();
        const isWeekend = dow === 0 || dow === 6;
        const isFerie = feriesMap.has(iso);
        if (absence) {
            // Clic sur une absence existante → ouvrir le détail
            setDetailRequest(absence.request);
            setDetailOpen(true);
            setSelectionStart(null);
            return;
        }
        if (isWeekend || isFerie)
            return;
        // Clic sur une cellule vide → sélection pour saisie
        if (!selectionStart) {
            // 1er clic : définir le début
            setSelectionStart(iso);
        }
        else {
            // 2e clic : définir la fin, ouvrir le dialog de création
            const start = selectionStart <= iso ? selectionStart : iso;
            const end = selectionStart <= iso ? iso : selectionStart;
            setCreateStartDate(start);
            setCreateEndDate(end);
            setCreateType("CP");
            setCreateComment("");
            setCreateOpen(true);
            setSelectionStart(null);
        }
    }, [absenceMap, feriesMap, selectionStart]);
    // Annuler la sélection en cours
    const cancelSelection = () => {
        setSelectionStart(null);
    };
    // === Actions sur une absence ===
    const handleAction = async (action) => {
        if (!apiToken || !detailRequest)
            return;
        setActionLoading(true);
        try {
            await updateLeaveRequestStatus(apiToken, detailRequest.id, action);
            setRequests((prev) => prev.map((r) => r.id === detailRequest.id ? { ...r, status: action } : r));
            setDetailOpen(false);
            const labels = { Approuve: "validée", Refuse: "refusée", Annule: "annulée" };
            showMessage(`Demande ${detailRequest.leaveType} du ${formatDateFR(detailRequest.startDate)} au ${formatDateFR(detailRequest.endDate)} ${labels[action]}`, "success");
        }
        catch (err) {
            showMessage(`Erreur : ${err instanceof Error ? err.message : "Erreur"}`, "error");
        }
        finally {
            setActionLoading(false);
        }
    };
    // === Création d'une absence ===
    const handleCreate = async () => {
        if (!apiToken || !currentEmployee)
            return;
        setCreateLoading(true);
        try {
            const days = countBusinessDays(createStartDate, createEndDate);
            // Trouver le manager du salarié
            const approb = approbateurs.find((a) => a.service === currentEmp?.service && a.role === "N+1");
            const id = await createLeaveRequest(apiToken, {
                EmployeeEmail: currentEmployee,
                EmployeeName: currentEmp?.name || currentEmployee,
                LeaveType: createType,
                StartDate: createStartDate,
                EndDate: createEndDate,
                EntryMode: "full",
                DaysCount: days,
                Status: "En attente",
                Comment: createComment,
                ManagerEmail: approb?.employeeEmail || "",
            });
            setRequests((prev) => [...prev, {
                    id,
                    employeeEmail: currentEmployee,
                    employeeName: currentEmp?.name || currentEmployee,
                    leaveType: createType,
                    startDate: createStartDate,
                    endDate: createEndDate,
                    entryMode: "full",
                    daysCount: days,
                    status: "En attente",
                    comment: createComment,
                    managerEmail: approb?.employeeEmail || "",
                    createdAt: new Date().toISOString(),
                }]);
            setCreateOpen(false);
            const typeLabel = TYPE_COLORS[createType]?.label || createType;
            showMessage(`Absence ${typeLabel} créée du ${formatDateFR(createStartDate)} au ${formatDateFR(createEndDate)} (${days} jour${days > 1 ? "s" : ""})`, "success");
        }
        catch (err) {
            showMessage(`Erreur : ${err instanceof Error ? err.message : "Erreur"}`, "error");
        }
        finally {
            setCreateLoading(false);
        }
    };
    if (loading)
        return _jsx(Spinner, { label: "Chargement du planning..." });
    return (_jsxs("div", { children: [!fixedEmployeeEmail && _jsx("h1", { style: { marginBottom: "8px" }, children: "Planning des absences" }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: success }) }), !fixedEmployeeEmail && (_jsxs("div", { style: { display: "flex", gap: "8px", marginBottom: "12px", alignItems: "center" }, children: [currentEmployee ? (_jsxs(_Fragment, { children: [_jsx(Badge, { appearance: "filled", color: "brand", style: { padding: "4px 12px", fontSize: "14px" }, children: employeeName }), (() => {
                                const svc = currentEmp?.service || "";
                                const n1 = approbateurs.find((a) => a.service === svc && a.role === "N+1");
                                const n2 = approbateurs.find((a) => a.service === svc && a.role === "N+2");
                                return (_jsxs(_Fragment, { children: [n1 && _jsxs(Text, { size: 200, style: { color: "#555" }, children: ["N+1 : ", _jsx("strong", { children: n1.employeeName })] }), n2 && _jsxs(Text, { size: 200, style: { color: "#555" }, children: ["N+2 : ", n2.employeeName] })] }));
                            })(), onNavigateToFiche && (_jsx(Button, { size: "small", appearance: "subtle", icon: _jsx(PersonRegular, {}), onClick: () => onNavigateToFiche(currentEmployee), children: "Fiche" })), _jsx(Button, { size: "small", appearance: "subtle", icon: _jsx(DismissCircleRegular, {}), onClick: () => { setSelectedEmployee(""); setSearchText(""); } })] })) : (_jsxs("div", { ref: pickerRef, style: { position: "relative", minWidth: "400px" }, children: [_jsx(Input, { contentBefore: _jsx(SearchRegular, {}), value: searchText, onChange: (_, d) => { setSearchText(d.value); setPickerOpen(true); }, onFocus: () => setPickerOpen(true), placeholder: "Rechercher un salari\u00E9 (nom ou email)...", style: { width: "100%" } }), pickerOpen && pickerResults.length > 0 && (_jsx("div", { style: {
                                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                                    background: "#fff", border: "1px solid #d1d5db", borderRadius: "4px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: "300px", overflowY: "auto",
                                }, children: pickerResults.map((e) => (_jsxs("div", { onClick: () => { setSelectedEmployee(e.email); setSearchText(""); setPickerOpen(false); }, style: {
                                        padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f3f4f6",
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                    }, onMouseEnter: (ev) => { ev.currentTarget.style.background = "#f0f7ff"; }, onMouseLeave: (ev) => { ev.currentTarget.style.background = "#fff"; }, children: [_jsxs("div", { children: [_jsx(Text, { weight: "semibold", size: 300, children: e.name }), _jsx(Text, { size: 200, style: { color: "#666", marginLeft: "8px" }, children: e.email })] }), _jsxs(Text, { size: 200, style: { color: "#999" }, children: [e.lieu ? `${e.lieu}` : "", e.service ? ` · ${e.service}` : ""] })] }, e.email))) }))] })), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), onClick: fetchData, appearance: "subtle", size: "small" })] })), !currentEmployee && !fixedEmployeeEmail ? (_jsx(Text, { size: 300, style: { color: "#666" }, children: "Tapez un nom pour rechercher un salari\u00E9." })) : (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }, children: [_jsxs(TabList, { selectedValue: mode, onTabSelect: (_, d) => setMode(d.value), children: [_jsx(Tab, { value: "week", children: "Semaine" }), _jsx(Tab, { value: "month", children: "Mois" }), _jsx(Tab, { value: "year", children: "Ann\u00E9e" })] }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [_jsx(Button, { icon: _jsx(ChevronLeftRegular, {}), appearance: "subtle", onClick: navigatePrev, size: "small" }), _jsxs(Text, { size: 400, weight: "bold", children: [periodLabel(), " \u2014 ", employeeName] }), _jsx(Button, { icon: _jsx(ChevronRightRegular, {}), appearance: "subtle", onClick: navigateNext, size: "small" })] })] }), _jsxs("div", { style: { display: "flex", gap: "24px", marginBottom: "8px" }, children: [_jsxs(Text, { size: 300, children: ["Valid\u00E9es : ", _jsxs("strong", { children: [approvedDays.toString().replace(".", ","), " jours"] })] }), _jsxs(Text, { size: 300, children: ["En attente : ", _jsxs("strong", { children: [pendingDays.toString().replace(".", ","), " jours"] })] })] }), selectionStart && (_jsxs("div", { style: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "8px", padding: "8px 12px", background: "#e0f2fe", borderRadius: "6px", border: "1px solid #7dd3fc" }, children: [_jsxs(Text, { size: 300, children: ["Date de d\u00E9but : ", _jsx("strong", { children: formatDateFR(selectionStart) }), " \u2014 Cliquez sur une autre date pour d\u00E9finir la fin"] }), _jsx(Button, { size: "small", appearance: "subtle", onClick: cancelSelection, children: "Annuler" })] })), mode === "year" && _jsx(YearView, { year: year, feriesMap: feriesMap, absenceMap: absenceMap, selectionStart: selectionStart, onCellClick: handleCellClick }), mode === "month" && _jsx(MonthView, { year: year, month: month, feriesMap: feriesMap, absenceMap: absenceMap, selectionStart: selectionStart, onCellClick: handleCellClick }), mode === "week" && _jsx(WeekView, { weekStart: weekStart, feriesMap: feriesMap, absenceMap: absenceMap, selectionStart: selectionStart, onCellClick: handleCellClick }), _jsx(Legend, {})] })), _jsx(Dialog, { open: detailOpen, onOpenChange: (_, d) => { if (!d.open)
                    setDetailOpen(false); }, children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: "D\u00E9tail de l'absence" }), _jsx(DialogContent, { children: detailRequest && (() => {
                                    const typeInfo = TYPE_COLORS[detailRequest.leaveType] || { bg: "#6b7280", text: "#fff", label: detailRequest.leaveType };
                                    const isPending = detailRequest.status.startsWith("En attente");
                                    const isApproved = detailRequest.status === "Approuvé" || detailRequest.status === "Approuve";
                                    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "12px" }, children: [_jsxs("div", { style: { display: "flex", gap: "12px", alignItems: "center" }, children: [_jsx("span", { style: { background: typeInfo.bg, color: typeInfo.text, padding: "4px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "14px" }, children: detailRequest.leaveType }), _jsx(Text, { size: 400, weight: "semibold", children: typeInfo.label })] }), _jsx(Field, { label: "Salari\u00E9", children: _jsxs(Text, { children: [detailRequest.employeeName, " (", detailRequest.employeeEmail, ")"] }) }), _jsxs("div", { style: { display: "flex", gap: "24px" }, children: [_jsx(Field, { label: "Du", children: _jsx(Text, { weight: "semibold", children: formatDateFR(detailRequest.startDate) }) }), _jsx(Field, { label: "Au", children: _jsx(Text, { weight: "semibold", children: formatDateFR(detailRequest.endDate) }) }), _jsx(Field, { label: "Jours", children: _jsx(Text, { weight: "semibold", children: detailRequest.daysCount }) })] }), _jsx(Field, { label: "Statut", children: _jsx(Badge, { color: isPending ? "warning" : isApproved ? "success" : "danger", appearance: "filled", children: detailRequest.status }) }), detailRequest.comment && (_jsx(Field, { label: "Commentaire", children: _jsx(Text, { children: detailRequest.comment }) })), detailRequest.managerEmail && (_jsx(Field, { label: "Manager", children: _jsx(Text, { children: detailRequest.managerEmail }) }))] }));
                                })() }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "secondary", onClick: () => setDetailOpen(false), children: "Fermer" }), detailRequest?.status.startsWith("En attente") && (_jsxs(_Fragment, { children: [_jsx(Button, { appearance: "primary", icon: _jsx(CheckmarkCircleRegular, {}), onClick: () => handleAction("Approuve"), disabled: actionLoading, children: "Valider" }), _jsx(Button, { icon: _jsx(DismissCircleRegular, {}), onClick: () => handleAction("Refuse"), disabled: actionLoading, style: { color: "#dc2626" }, children: "Refuser" })] })), (detailRequest?.status === "Approuvé" || detailRequest?.status === "Approuve") && (_jsx(Button, { icon: _jsx(DeleteRegular, {}), onClick: () => handleAction("Annule"), disabled: actionLoading, style: { color: "#dc2626" }, children: "Annuler cette absence" }))] })] }) }) }), _jsx(Dialog, { open: createOpen, onOpenChange: (_, d) => { if (!d.open) {
                    setCreateOpen(false);
                    setSelectionStart(null);
                } }, children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: "Nouvelle absence" }), _jsx(DialogContent, { children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "12px" }, children: [_jsx(Field, { label: "Salari\u00E9", children: _jsx(Text, { weight: "semibold", children: employeeName }) }), _jsxs("div", { style: { display: "flex", gap: "16px" }, children: [_jsx(Field, { label: "Date de d\u00E9but", children: _jsx(Text, { weight: "semibold", size: 400, children: formatDateFR(createStartDate) }) }), _jsx(Field, { label: "Date de fin", children: _jsx(Text, { weight: "semibold", size: 400, children: formatDateFR(createEndDate) }) }), _jsx(Field, { label: "Jours ouvr\u00E9s", children: _jsx(Text, { weight: "semibold", size: 400, children: createStartDate && createEndDate ? countBusinessDays(createStartDate, createEndDate) : 0 }) })] }), _jsx(Field, { label: "Type d'absence", required: true, children: _jsx(Dropdown, { value: LEAVE_TYPE_OPTIONS.find((o) => o.value === createType)?.label || "", selectedOptions: [createType], onOptionSelect: (_, d) => setCreateType(d.optionValue || "CP"), children: LEAVE_TYPE_OPTIONS.map((o) => (_jsx(Option, { value: o.value, text: o.label, children: _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: "8px" }, children: [_jsx("span", { style: {
                                                                    width: "12px", height: "12px", borderRadius: "2px",
                                                                    background: TYPE_COLORS[o.value]?.bg || "#999",
                                                                    display: "inline-block",
                                                                } }), o.label] }) }, o.value))) }) }), _jsx(Field, { label: "Commentaire", children: _jsx(Textarea, { value: createComment, onChange: (_, d) => setCreateComment(d.value), placeholder: "Motif de l'absence (optionnel)", rows: 2 }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "secondary", onClick: () => { setCreateOpen(false); setSelectionStart(null); }, children: "Annuler" }), _jsx(Button, { appearance: "primary", onClick: handleCreate, disabled: createLoading || !createType, children: createLoading ? "Création..." : "Créer l'absence" })] })] }) }) })] }));
};
const SELECTION_STYLE = { outline: "3px solid #0066cc", outlineOffset: "-2px", zIndex: 2 };
const YearView = ({ year, feriesMap, absenceMap, selectionStart, onCellClick }) => (_jsx("div", { style: { overflowX: "auto" }, children: _jsxs("table", { style: { borderCollapse: "collapse", fontSize: "11px", width: "100%", tableLayout: "fixed" }, children: [_jsx("thead", { children: _jsxs("tr", { style: { borderBottom: "2px solid #0066cc" }, children: [_jsx("th", { style: { ...stickyCol, width: "30px", background: "#fff" } }), MOIS_COURTS.map((m, i) => (_jsx("th", { style: { padding: "6px 2px", textAlign: "center", fontWeight: 700, color: "#0066cc", fontSize: "11px" }, children: m }, i)))] }) }), _jsx("tbody", { children: Array.from({ length: 31 }, (_, dayIdx) => {
                    const dayNum = dayIdx + 1;
                    return (_jsxs("tr", { style: { borderBottom: "1px solid #e5e7eb" }, children: [_jsx("td", { style: { ...stickyCol, fontWeight: 700, textAlign: "right", paddingRight: "6px", fontSize: "11px", background: "#fff" }, children: dayNum }), Array.from({ length: 12 }, (_, monthIdx) => {
                                const daysInMonth = getDaysInMonth(year, monthIdx);
                                if (dayNum > daysInMonth) {
                                    return _jsx("td", { style: { background: "#f9fafb", border: "1px solid #f3f4f6" } }, monthIdx);
                                }
                                const iso = toISO(year, monthIdx, dayNum);
                                const dow = new Date(year, monthIdx, dayNum).getDay();
                                const cell = getCellStyle(iso, dow, feriesMap, absenceMap);
                                const isSelected = iso === selectionStart;
                                const isWeekend = dow === 0 || dow === 6;
                                const isFerie = feriesMap.has(iso);
                                const hasAbsence = absenceMap.has(iso);
                                const isClickable = hasAbsence || (!isWeekend && !isFerie);
                                return (_jsx("td", { style: { padding: 0, border: cell.border || "1px solid #e5e7eb", position: "relative" }, children: _jsx(Tooltip, { content: `${dayNum} ${MOIS_COURTS[monthIdx]} ${year} — ${JOURS_COURTS[dow === 0 ? 6 : dow - 1]}${cell.tooltip ? ` · ${cell.tooltip}` : ""}`, relationship: "label", children: _jsx("div", { onClick: () => isClickable && onCellClick(iso), style: {
                                                background: cell.bg,
                                                color: cell.color,
                                                padding: "1px 2px",
                                                textAlign: "center",
                                                fontSize: "10px",
                                                fontWeight: cell.fontWeight,
                                                minHeight: "18px",
                                                lineHeight: "16px",
                                                cursor: isClickable ? "pointer" : "default",
                                                ...(cell.isPending ? { backgroundImage: PENDING_STRIPE, fontStyle: "italic" } : {}),
                                                ...(isSelected ? SELECTION_STYLE : {}),
                                            }, children: cell.isPending ? `${cell.label}?` : cell.label }) }) }, monthIdx));
                            })] }, dayNum));
                }) })] }) }));
// ==================== VUE MOIS ====================
const MonthView = ({ year, month, feriesMap, absenceMap, selectionStart, onCellClick }) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDow = new Date(year, month, 1).getDay();
    const startOffset = firstDow === 0 ? 6 : firstDow - 1;
    const cells = [];
    for (let i = 0; i < startOffset; i++)
        cells.push(null);
    for (let d = 1; d <= daysInMonth; d++)
        cells.push(d);
    while (cells.length % 7 !== 0)
        cells.push(null);
    return (_jsx("div", { style: { overflowX: "auto" }, children: _jsxs("table", { style: { borderCollapse: "collapse", width: "100%", maxWidth: "800px" }, children: [_jsx("thead", { children: _jsx("tr", { children: JOURS_COMPLETS.map((j) => (_jsx("th", { style: { padding: "8px 4px", textAlign: "center", fontWeight: 600, borderBottom: "2px solid #0066cc", color: "#0066cc", fontSize: "13px" }, children: j }, j))) }) }), _jsx("tbody", { children: Array.from({ length: cells.length / 7 }, (_, rowIdx) => (_jsx("tr", { children: Array.from({ length: 7 }, (_, colIdx) => {
                            const dayNum = cells[rowIdx * 7 + colIdx];
                            if (dayNum === null) {
                                return _jsx("td", { style: { border: "1px solid #f3f4f6", height: "70px", background: "#fafafa" } }, colIdx);
                            }
                            const iso = toISO(year, month, dayNum);
                            const dow = new Date(year, month, dayNum).getDay();
                            const cell = getCellStyle(iso, dow, feriesMap, absenceMap);
                            const isSelected = iso === selectionStart;
                            const isWeekend = dow === 0 || dow === 6;
                            const isFerie = feriesMap.has(iso);
                            const hasAbsence = absenceMap.has(iso);
                            const isClickable = hasAbsence || (!isWeekend && !isFerie);
                            return (_jsxs("td", { onClick: () => isClickable && onCellClick(iso), style: {
                                    border: "1px solid #e5e7eb",
                                    height: "70px",
                                    verticalAlign: "top",
                                    padding: "4px",
                                    background: cell.bg === "#fff" ? "#fff" : undefined,
                                    cursor: isClickable ? "pointer" : "default",
                                    ...(isSelected ? SELECTION_STYLE : {}),
                                }, children: [_jsx("div", { style: { fontSize: "12px", fontWeight: 600, marginBottom: "4px", color: "#374151" }, children: dayNum }), (cell.label !== JOURS_COURTS[dow === 0 ? 6 : dow - 1] || cell.bg !== "#fff") && (_jsx(Tooltip, { content: cell.tooltip || cell.label, relationship: "label", children: _jsx("div", { style: {
                                                background: cell.bg,
                                                color: cell.color,
                                                padding: "2px 6px",
                                                borderRadius: "4px",
                                                fontSize: "11px",
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                ...(cell.isPending ? {
                                                    backgroundImage: PENDING_STRIPE,
                                                    border: cell.border,
                                                    fontStyle: "italic",
                                                } : {}),
                                            }, children: cell.isPending ? `${cell.label} ?` : cell.label }) }))] }, colIdx));
                        }) }, rowIdx))) })] }) }));
};
// ==================== VUE SEMAINE ====================
const WeekView = ({ weekStart, feriesMap, absenceMap, selectionStart, onCellClick }) => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return (_jsx("div", { style: { overflowX: "auto" }, children: _jsxs("table", { style: { borderCollapse: "collapse", width: "100%", maxWidth: "900px" }, children: [_jsx("thead", { children: _jsx("tr", { children: days.map((d, i) => (_jsxs("th", { style: {
                                padding: "10px 8px",
                                textAlign: "center",
                                fontWeight: 600,
                                borderBottom: "2px solid #0066cc",
                                color: "#0066cc",
                                fontSize: "13px",
                                width: `${100 / 7}%`,
                            }, children: [JOURS_COMPLETS[i], _jsx("br", {}), _jsx("span", { style: { fontSize: "16px", color: "#374151" }, children: d.getDate() }), _jsxs("span", { style: { fontSize: "11px", color: "#999" }, children: [" ", MOIS_COURTS[d.getMonth()]] })] }, i))) }) }), _jsx("tbody", { children: _jsx("tr", { children: days.map((d, i) => {
                            const iso = toISO(d.getFullYear(), d.getMonth(), d.getDate());
                            const dow = d.getDay();
                            const cell = getCellStyle(iso, dow, feriesMap, absenceMap);
                            const isSpecial = cell.label !== JOURS_COURTS[dow === 0 ? 6 : dow - 1] || cell.bg !== "#fff";
                            const isSelected = iso === selectionStart;
                            const isWeekend = dow === 0 || dow === 6;
                            const isFerie = feriesMap.has(iso);
                            const hasAbsence = absenceMap.has(iso);
                            const isClickable = hasAbsence || (!isWeekend && !isFerie);
                            return (_jsx("td", { onClick: () => isClickable && onCellClick(iso), style: {
                                    border: "1px solid #e5e7eb",
                                    height: "120px",
                                    verticalAlign: "top",
                                    padding: "8px",
                                    background: (dow === 0 || dow === 6) ? WE_COLOR.bg : "#fff",
                                    cursor: isClickable ? "pointer" : "default",
                                    ...(isSelected ? SELECTION_STYLE : {}),
                                }, children: isSpecial && (_jsx(Tooltip, { content: cell.tooltip || cell.label, relationship: "label", children: _jsx("div", { style: {
                                            background: cell.bg,
                                            color: cell.color,
                                            padding: "6px 8px",
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            marginTop: "8px",
                                            ...(cell.isPending ? {
                                                backgroundImage: PENDING_STRIPE,
                                                border: cell.border,
                                                fontStyle: "italic",
                                            } : {}),
                                        }, children: cell.isPending ? `${cell.label} ?` : cell.label }) })) }, i));
                        }) }) })] }) }));
};
// ==================== LÉGENDE ====================
const Legend = () => (_jsxs("div", { style: { marginTop: "20px", padding: "12px 16px", background: "#f9fafb", borderRadius: "6px", border: "1px solid #e5e7eb" }, children: [_jsx(Text, { weight: "semibold", size: 300, style: { marginBottom: "8px", display: "block" }, children: "L\u00E9gende" }), _jsxs("div", { style: { display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "12px" }, children: [_jsx(LegendItem, { bg: JF_COLOR.bg, color: JF_COLOR.text, label: "JF", description: "Jour f\u00E9ri\u00E9" }), _jsx(LegendItem, { bg: PONT_COLOR.bg, color: PONT_COLOR.text, label: "P", description: "Jour de pont" }), _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsx("span", { style: { width: "20px", height: "14px", border: TODAY_BORDER, borderRadius: "2px", display: "inline-block" } }), _jsx("span", { style: { fontSize: "11px" }, children: "Aujourd'hui" })] }), Object.entries(TYPE_COLORS).map(([code, style]) => (_jsx(LegendItem, { bg: style.bg, color: style.text, label: code, description: style.label }, code))), _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsx("span", { style: {
                                width: "40px", height: "16px", background: lightenColor("#00bfff", 0.55),
                                backgroundImage: PENDING_STRIPE,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                borderRadius: "2px", border: "2px dashed #00bfff",
                                fontSize: "8px", fontWeight: "bold", fontStyle: "italic", color: "#555",
                            }, children: "CP ?" }), _jsx("span", { style: { fontSize: "11px" }, children: "En attente de validation" })] }), _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsx("span", { style: {
                                width: "40px", height: "16px", background: "#00bfff",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                borderRadius: "2px", border: "1px solid #ddd",
                                fontSize: "8px", fontWeight: "bold", color: "#000",
                            }, children: "CP" }), _jsx("span", { style: { fontSize: "11px" }, children: "Approuv\u00E9" })] })] })] }));
const LegendItem = ({ bg, color, label, description }) => (_jsx(Tooltip, { content: description, relationship: "label", children: _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: "4px" }, children: [_jsx("span", { style: { width: "20px", height: "14px", background: bg, color, fontSize: "8px", fontWeight: "bold", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "2px", border: "1px solid #ddd" }, children: label }), _jsx("span", { style: { fontSize: "11px" }, children: description })] }) }));
const stickyCol = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    padding: "2px 4px",
};
